import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import Swal from 'sweetalert2';

/**
 * Keeps an installed copy of the app from going stale.
 *
 * <p>This is the half of the PWA work that actually matters operationally. A service
 * worker serves the bundle it already has, so after a customer upgrade the browser can
 * keep running the OLD app indefinitely — the user reports that a fix "isn't there"
 * and nobody can reproduce it. Angular downloads the new version in the background;
 * what it will not do on its own is tell anyone, or swap it in.</p>
 *
 * <p>So: check every 30 minutes (and once on startup), and when a new version is ready
 * ask the user to reload. The prompt is deliberately a question rather than a forced
 * reload — someone half way through a flow in the designer should not lose it to a
 * deployment.</p>
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  /** How often to ask the server whether a newer build exists. */
  private static readonly CHECK_INTERVAL_MS = 30 * 60 * 1000;

  constructor(private updates: SwUpdate, private appRef: ApplicationRef) {}

  init(): void {
    // `isEnabled` is false during `ng serve` and on any build without the service
    // worker, so this whole service is inert in local development.
    if (!this.updates.isEnabled) {
      return;
    }

    this.pollForUpdates();

    this.updates.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.promptReload());

    // The worker can end up with a broken or missing bundle — the only way out is a
    // hard reload from the network, otherwise the app is stuck on a blank page.
    this.updates.unrecoverable.subscribe(() => {
      Swal.fire({
        title: 'Reload required',
        text: 'The app could not load properly. Reloading will fix it.',
        icon: 'warning',
        allowOutsideClick: false,
        confirmButtonText: 'Reload'
      }).then(() => document.location.reload());
    });
  }

  /**
   * Poll only once the app has settled.
   *
   * <p>`isStable` matters: an Angular app that never becomes stable — and one with
   * long-lived polling would not — makes an `interval` fire against a busy zone. Waiting
   * for the first stable tick and only then starting the timer is the documented
   * pattern.</p>
   */
  private pollForUpdates(): void {
    const appIsStable$ = this.appRef.isStable.pipe(first(stable => stable === true));
    const everyInterval$ = interval(AppUpdateService.CHECK_INTERVAL_MS);

    concat(appIsStable$, everyInterval$).subscribe(() => {
      this.updates.checkForUpdate().catch(() => {
        // Offline or the server is briefly unreachable. Nothing to do — the next
        // tick will try again, and a failed check must never surface to the user.
      });
    });
  }

  private promptReload(): void {
    Swal.fire({
      title: 'A new version is available',
      text: 'Reload to get the latest version. Unsaved work on this page will be lost.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Reload now',
      cancelButtonText: 'Later',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then(result => {
      if (result.isConfirmed) {
        // activateUpdate() swaps the worker; the reload is what puts the new bundle
        // on screen. Reloading without activating would just serve the old one again.
        this.updates.activateUpdate().then(() => document.location.reload());
      }
    });
  }
}
